import { Box, Typography, useTheme } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import FlatSwitch from "../common/FlatSwitch";
import AlertGroupCard from "../Notifications/AlertGroupCard";
import NotificationAvatar from "../Notifications/NotificationAvatar";
import PushSoftAskSheet from "../Notifications/PushSoftAskSheet";
import { hapticSelection } from "../../lib/haptics";
import { isNative } from "../../lib/platform";
import { useSavedScreens } from "./hooks/useSavedScreens";
import { useScreenAlertToggle } from "./hooks/useScreenAlertToggle";
import { useScreenerUsage } from "./hooks/useScreenerUsage";
import { ALERT_FREQUENCY, alertStatusLine, alertsOn } from "./screenAlertCopy";
import type { SavedScreenSummary } from "./types";

const statusFor = (screen: SavedScreenSummary, isPro: boolean): string => {
  if (screen.invalid) return "Needs updating";
  if (screen.alertStatus === "active")
    return `New matches · ${ALERT_FREQUENCY}`;
  if (screen.alertStatus === "off") return "Off";
  return alertStatusLine(screen.alertStatus, isPro);
};

/**
 * Manage alerts' "Saved screens" group, read from the screens themselves (a
 * screen's alerts are a switch on the screen, not a subscription): each by
 * name, with its own switch on Pro. A downgraded member sees only the screens
 * whose alerts are paused, with no switch.
 */
export const SavedScreenAlertsGroup = () => {
  const theme = useTheme();
  const { data } = useSavedScreens();
  const { data: allowance } = useScreenerUsage();
  const alerts = useScreenAlertToggle();

  const isPro = allowance?.tier === "pro" && allowance.blocked !== true;
  const screens = (data?.screens ?? []).filter(
    (screen) => isPro || screen.alertStatus === "paused_plan",
  );
  if (screens.length === 0) return null;

  return (
    <>
      <AlertGroupCard title="Saved screens">
        {screens.map((screen) => (
          <Box
            key={screen.id}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: 1.5,
              py: 1,
            }}
          >
            <Box
              component={RouterLink}
              to={`/screener/s/${screen.id}`}
              aria-label={`Open ${screen.name}`}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flex: 1,
                minWidth: 0,
                minHeight: 44,
                borderRadius: 2,
                color: "inherit",
                textDecoration: "none",
                "&:hover .alert-row-label": { textDecoration: "underline" },
                "&:focus-visible": {
                  outline: `2px solid ${theme.palette.primary.main}`,
                  outlineOffset: 2,
                },
              }}
            >
              <NotificationAvatar imageKind="icon" imageValue="screener" />
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  noWrap
                  className="alert-row-label"
                  sx={{
                    fontSize: "0.9375rem",
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                  }}
                >
                  {screen.name}
                </Typography>
                <Typography
                  noWrap
                  sx={{
                    fontSize: "0.8125rem",
                    color: theme.palette.text.secondary,
                  }}
                >
                  {statusFor(screen, isPro)}
                </Typography>
              </Box>
            </Box>
            {isPro && (
              <Box
                component="label"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: 44,
                  minHeight: 44,
                  flexShrink: 0,
                  cursor: "pointer",
                }}
              >
                <FlatSwitch
                  checked={alertsOn(screen.alertStatus)}
                  disabled={
                    alerts.isPending ||
                    (screen.invalid && !alertsOn(screen.alertStatus))
                  }
                  ariaLabel={`Alerts for ${screen.name}`}
                  onChange={(enabled) => {
                    hapticSelection();
                    alerts.toggle(screen.id, enabled);
                  }}
                />
              </Box>
            )}
          </Box>
        ))}
      </AlertGroupCard>
      {isNative() && (
        <PushSoftAskSheet
          variant="screener"
          open={alerts.softAskOpen}
          onClose={alerts.closeSoftAsk}
        />
      )}
    </>
  );
};
